import React, { useState, useEffect, useContext } from 'react';
import createAuth0Client from '@auth0/auth0-spa-js';
import * as Sentry from '@sentry/react';

import _ from 'lodash';
import { setJwtApiToken, clearStore, getJwtApiToken } from './utils';
import { ENV } from './config';

const IS_E2E = ENV.IS_E2E;

const DEFAULT_REDIRECT_CALLBACK = () => window.history.replaceState({}, document.title, window.location.pathname);

const getUserFromAuth0User = (auth0User:any) => _.get(auth0User, 'https://keystonestrategy.io/user', null);
const setAuth0UserOnSentry = (auth0User:any) => {
  if (!auth0User) return;

  const user = getUserFromAuth0User(auth0User);
  if (user === null) return;
  Sentry.configureScope((scope) => {
    scope.setUser({
      _id: user._id,
      email: user.email,
    });
  });
};

export const Auth0Context = React.createContext<any>({});
export const useAuth0 = () => useContext(Auth0Context);
export const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  ...initOptions
}:any) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<any>();
  const [auth0Client, setAuth0] = useState<any>();
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const initAuth0 = async () => {
      setLoading(true);
      const auth0FromHook = await createAuth0Client(initOptions);
      setAuth0(auth0FromHook);

      if (window.location.search.includes('code=')
          && window.location.search.includes('state=')) {
        const { appState } = await auth0FromHook.handleRedirectCallback();
        onRedirectCallback(appState);
      }
      const isTokenStored = getJwtApiToken()!!;

      const isE2EandAuthenticated = IS_E2E && isTokenStored;
      const auth0IsAuthenticated = isE2EandAuthenticated ? true : await auth0FromHook.isAuthenticated();

      setIsAuthenticated(auth0IsAuthenticated);

      if (auth0IsAuthenticated && !isE2EandAuthenticated) {
        const auth0User = await auth0FromHook.getUser();
        const token = await auth0FromHook.getTokenSilently();
        setJwtApiToken(token);
        setUser(auth0User);
        setAuth0UserOnSentry(auth0User);
      }
      setLoading(false);
    };
    initAuth0();
    // eslint-disable-next-line
  }, []);

  const handleRedirectCallback = async () => {
    setLoading(true);
    await auth0Client.handleRedirectCallback();
    const auth0User = await auth0Client.getUser();
    setLoading(false);
    setIsAuthenticated(true);
    setUser(auth0User);
    setAuth0UserOnSentry(auth0User);
  };
  const value:any = {
    isAuthenticated,
    user,
    loading,
    handleRedirectCallback,
    getIdTokenClaims: (...p:any) => auth0Client.getIdTokenClaims(...p),
    loginWithRedirect: (...p:any) => auth0Client.loginWithRedirect({ ...p, connection_scope: 'offline_access' }),
    getTokenSilently: (...p:any) => auth0Client.getTokenSilently(...p),
    logout: (...p:any) => {
      clearStore();
      auth0Client.logout(...p);
    },
  };
  return (
    <Auth0Context.Provider
      value={value}
    >
      {children}
    </Auth0Context.Provider>
  );
};
